import axios from 'axios';
import { useAuthStore } from '../stores/authStore';

// Função para obter a URL da API dinamicamente
// Esta função será chamada a cada requisição, garantindo que sempre use a URL correta
const getApiUrl = () => {
  // Prioridade 1: URL definida pelo WordPress via data-api-url
  if (window.HAKLAI_API_URL) {
    return window.HAKLAI_API_URL;
  }
  
  // Prioridade 2: Tentar ler do elemento DOM (caso ainda não tenha sido definido)
  const appElement = document.getElementById('haklai-app');
  if (appElement?.dataset?.apiUrl) {
    return appElement.dataset.apiUrl;
  }
  
  // Prioridade 3: Fallback usando caminho relativo do WordPress
  // Isso funciona porque o WordPress sempre está acessível via /wp-json/
  // independente de estar na raiz ou em subdiretório
  return window.location.origin + '/wp-json/haklai/v1/';
};

const api = axios.create({
  baseURL: '', // Será definido dinamicamente no interceptor
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para definir baseURL dinamicamente ANTES de cada requisição
api.interceptors.request.use(
  (config) => {
    // Define baseURL dinamicamente se ainda não foi definido ou se mudou
    config.baseURL = getApiUrl();
    
    // Se for FormData, remove Content-Type para o browser definir automaticamente
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Import the checkpoint auth store dynamically to avoid circular dependencies if needed, or directly
import { useCheckpointAuthStore } from '../stores/checkpointAuthStore';

// Interceptor para adicionar token e tenant_id
api.interceptors.request.use(
  (config) => {
    // Detecta se estamos rodando dentro do shortcode
    const isCheckpointContext = !!document.getElementById('haklai-checkpoint');
    
    let token, tenantId;
    
    if (isCheckpointContext) {
      const state = useCheckpointAuthStore.getState();
      token = state.token;
      tenantId = state.tenantId;
    } else {
      const state = useAuthStore.getState();
      token = state.token;
      tenantId = state.tenantId;
    }
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    if (tenantId) {
      config.headers['X-Tenant-ID'] = tenantId;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratar erros de autenticação
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Se erro 401 e não foi tentativa de refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const isCheckpointContext = !!document.getElementById('haklai-checkpoint');
      let refreshed;
      if (isCheckpointContext) {
        refreshed = await useCheckpointAuthStore.getState().refreshAccessToken();
      } else {
        refreshed = await useAuthStore.getState().refreshAccessToken();
      }
      
      if (refreshed) {
        // Tenta novamente a requisição original
        return api(originalRequest);
      }
    }
    
    return Promise.reject(error);
  }
);

export default api;

